import React from "react";
import { Button } from "../../../../../shadcn/components/ui/button";
import DocIcon from "../../../../../icons/DocIcon";

interface KycModeSelectionProps {
  onSelectMode: (mode: "offline" | "online") => void;
  selectingMode: boolean;
}

export default function KycModeSelection({
  onSelectMode,
  selectingMode,
}: KycModeSelectionProps) {
  return (
    <div className="bg-white px-4 py-8 md:px-6 md:py-8 font-openSans mb-6">
      <div className="w-full">
        <div className="text-center mb-6">
          <h4 className="text-base md:text-2xl font-bold text-brand-textBlack mb-2">
            Choose your KYC mode
          </h4>
          <p className="text-xs md:text-sm text-brand-textGrey">
            Please select one option to continue with your bidder registration.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Offline KYC Card */}
          <div className="bg-brand-lightOrange/60 rounded-sm md:rounded-xl p-6 flex flex-col h-full transition-all hover:shadow-lg">
            <div className="flex items-center mb-2">
              <div className="bg-white/50 p-2 rounded-lg mr-3">
                <DocIcon width={20} height={20} className="text-gray-500" />
              </div>
              <h4 className="text-sm md:text-lg font-semibold text-brand-orange">
                Offline KYC
              </h4>
            </div>
            <p className="text-xs md:text-sm text-brand-textGrey mb-4">
              Complete your KYC by uploading documents for manual verification.
            </p>

            <ul className="space-y-2 md:space-y-3 mb-6 flex-grow">
              <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                <span className="text-brand-orange mr-2">•</span>
                <span>
                  PAN + one valid ID proof (Aadhaar / Passport / DL / Voter ID)
                </span>
              </li>
              <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                <span className="text-brand-orange mr-2">•</span>
                <span>Address proof if different from ID</span>
              </li>
              <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                <span className="text-brand-orange mr-2">•</span>
                <span>Authorised officer will verify and approve</span>
              </li>
            </ul>
            <Button
              onClick={() => onSelectMode("offline")}
              disabled={selectingMode}
              variant="outline"
              className="w-full"
            >
              {selectingMode ? "Processing..." : "Continue with Offline KYC"}
            </Button>
          </div>

          {/* Online KYC Card */}
          <div className="bg-brand-lightOrange/60 rounded-lg md:rounded-xl p-6 flex flex-col h-full transition-all hover:shadow-lg">
            <div className="flex items-center mb-2">
              <div className="bg-white/50 p-2 rounded-lg mr-3">
                <div style={{ color: "#FF752C" }}>
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    width={20}
                    height={20}
                    fill="none"
                    viewBox="0 0 32 32"
                  >
                    <path
                      d="M26.7322 15.6836L12.7322 30.6836C12.5838 30.8419 12.388 30.9477 12.1742 30.985C11.9604 31.0222 11.7404 30.989 11.5472 30.8902C11.354 30.7914 11.1982 30.6325 11.1032 30.4374C11.0083 30.2423 10.9794 30.0216 11.0209 29.8086L12.8534 20.6423L5.64965 17.9373C5.49494 17.8795 5.35697 17.7842 5.24808 17.66C5.13918 17.5358 5.06275 17.3865 5.02561 17.2256C4.98847 17.0646 4.99177 16.897 5.03523 16.7376C5.07869 16.5783 5.16094 16.4321 5.27465 16.3123L19.2747 1.31233C19.423 1.154 19.6188 1.04822 19.8326 1.01096C20.0464 0.973688 20.2664 1.00695 20.4596 1.10574C20.6528 1.20452 20.8086 1.36345 20.9036 1.55856C20.9985 1.75367 21.0274 1.97436 20.9859 2.18733L19.1484 11.3636L26.3521 14.0648C26.5057 14.1231 26.6426 14.2183 26.7506 14.342C26.8587 14.4657 26.9346 14.6141 26.9717 14.7741C27.0087 14.9341 27.0058 15.1008 26.9632 15.2594C26.9205 15.4181 26.8395 15.5637 26.7271 15.6836H26.7322Z"
                      fill="#FF752C"
                    />
                  </svg>
                </div>
              </div>
              <h4 className="text-sm md:text-lg font-semibold text-brand-orange">
                Online KYC
              </h4>
            </div>

            <p className="text-xs md:text-sm text-brand-textGrey mb-4">
              Fast, digital KYC using Aadhaar & DigiLocker.
            </p>

            <ul className="space-y-2 mb-4">
              <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                <span className="text-brand-orange mr-2">•</span>
                <span>Instant Aadhaar-based verification</span>
              </li>
              <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                <span className="text-brand-orange mr-2">•</span>
                <span>Auto-fetch details via DigiLocker</span>
              </li>
            </ul>

            <div className="border-2 border-dashed border-brand-orange rounded-sm px-4 py-2 mb-6 bg-white/50">
              <p className="font-bold text-brand-orange text-sm md:text-base mb-1">
                Important:
              </p>
              <p className="text-xs md:text-sm text-brand-textBlack mb-2">
                Online KYC is possible only if:
              </p>
              <ul className="space-y-1 md:space-y-2">
                <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                  <span className="text-brand-orange mr-2">•</span>
                  <span>
                    Your mobile number is{" "}
                    <span className="font-bold">
                      active & linked to Aadhaar
                    </span>
                  </span>
                </li>
                <li className="flex items-start text-xs md:text-sm text-brand-textBlack">
                  <span className="text-brand-orange mr-2">•</span>
                  <span>
                    You have access to the{" "}
                    <span className="font-bold">DigiLocker</span> system
                  </span>
                </li>
              </ul>
            </div>
            <Button
              onClick={() => onSelectMode("online")}
              disabled={selectingMode}
              className="w-full"
            >
              {selectingMode ? "Processing..." : "Continue with Online KYC"}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
